import { useEffect, useState } from 'react';
import {
  MdCategory,
  MdCurrencyRupee,
  MdHourglassEmpty,
  MdInventory2,
  MdShoppingCart,
} from 'react-icons/md';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { api } from '../../../apis/api';
import './dashboard.css';
import '../orders/orders.css';

const formatINR = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const RANGE_OPTIONS = [
  { value: 'week', label: 'Weeks' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];

const RANGE_TITLES = {
  week: 'Last 7 Days',
  month: 'Last 30 Days',
  year: 'Last 12 Months',
};

export const Dashboard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('week');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.getDashboard({ range });
        if (!cancelled) {
          setData(res.data);
          setError('');
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range]);

  if (loading && !data) return <p className="admin-loading">Loading dashboard…</p>;
  if (error && !data) return <p className="admin-error">{error}</p>;
  if (!data) return null;

  const stats = [
    { icon: <MdCurrencyRupee />, label: 'Total Revenue', value: formatINR(data.total_revenue) },
    { icon: <MdShoppingCart />, label: 'Orders', value: String(data.total_orders) },
    { icon: <MdHourglassEmpty />, label: 'Pending Orders', value: String(data.pending_orders) },
    { icon: <MdInventory2 />, label: 'Products', value: String(data.total_products) },
    { icon: <MdCategory />, label: 'Categories', value: String(data.total_categories) },
  ];

  const chartData = data.chartSeries || data.last7Days || [];
  const periodLabel = RANGE_TITLES[range] || RANGE_TITLES.week;
  const latest = chartData[chartData.length - 1] || { orders: 0 };
  const previous = chartData[chartData.length - 2] || { orders: 0 };
  const periodOrders = chartData.reduce((s, d) => s + Number(d.orders || 0), 0);
  const periodRevenue = chartData.reduce((s, d) => s + Number(d.sales || 0), 0);

  const RangeFilter = () => (
    <div className="dash__range" role="group" aria-label="Chart range">
      {RANGE_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`dash__range-btn${range === opt.value ? ' is-active' : ''}`}
          onClick={() => setRange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className={`dash${loading ? ' dash--loading' : ''}`}>
      <div className="dash__stats">
        {stats.map((s) => (
          <div className="dash__stat-card" key={s.label}>
            <div className="dash__stat-top">
              <span className="dash__stat-icon">{s.icon}</span>
            </div>
            <div className="dash__stat-value">{s.value}</div>
            <div className="dash__stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="dash__charts">
        <div className="dash__chart-card dash__chart-card--wide">
          <div className="dash__chart-header">
            <span className="dash__chart-title">Orders ({periodLabel})</span>
            <RangeFilter />
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} interval={range === 'month' ? 4 : 0} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="orders" stroke="#ff6b00" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
          <div className="dash__chart-footer">
            {range === 'week' ? (
              <>
                <strong>{latest.orders} Orders</strong> today &nbsp;&nbsp;
                <strong>{previous.orders} Orders</strong> yesterday
              </>
            ) : (
              <>
                <strong>{periodOrders} Orders</strong> in selected period
              </>
            )}
          </div>
        </div>

        <div className="dash__chart-card">
          <div className="dash__chart-header">
            <span className="dash__chart-title">{periodLabel} Sales</span>
          </div>
          <div className="dash__sales-summary">
            <div>
              <div className="dash__sales-sub">ORDERS</div>
              <div className="dash__sales-val">{periodOrders}</div>
            </div>
            <div>
              <div className="dash__sales-sub">REVENUE</div>
              <div className="dash__sales-val">{formatINR(periodRevenue)}</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={chartData}>
              <XAxis dataKey="day" tick={{ fontSize: 11 }} interval={range === 'month' ? 4 : 0} />
              <Tooltip />
              <Bar dataKey="sales" fill="#ff6b00" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="dash__recent admin-table-wrap" style={{ marginTop: 24 }}>
        <div className="dash__chart-title" style={{ padding: '16px 20px 0' }}>Recent Orders</div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>INVOICE</th>
              <th>CUSTOMER</th>
              <th>STATUS</th>
              <th>PAYMENT</th>
              <th>TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {(data.recentOrders || []).length === 0 ? (
              <tr><td colSpan={5}>No orders yet</td></tr>
            ) : (
              data.recentOrders.map((o) => (
                <tr key={o.order_id}>
                  <td>{o.invoice_no}</td>
                  <td>{o.customer_name}</td>
                  <td>
                    <span className={`status-badge ${o.status === 'enquire' ? 'status--enquire' : 'status--pending'}`}>
                      {o.status}
                    </span>
                  </td>
                  <td>{o.payment_status}</td>
                  <td>{formatINR(o.total)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
